import { ErrorHandler, NgModule, inject, provideAppInitializer } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";

import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { GlobalError } from "src/ancestors/exception.service";
import { LoggerService } from "src/ancestors/logger.service";
import { BackendService } from "src/app/global-service/backend-api.service";
import { BaseComponent } from "../ancestors/base/base.component";
import { BaseModule } from "../ancestors/base/base.module";
import { EnvironmentLoaderService } from "../ancestors/env-config.service";
import { i18nModule } from "../ancestors/i18n/i18n.module";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { ApplicationDataService } from "./global-service/application-data.service";
import { TokenManagerService } from "./global-service/token-manager.service";
import { TokenStorageService } from "./global-service/token-storage.service";
import { UserService } from "./global-service/user.service";
import { TenantService } from "./global-service/tenant.service";
import { CheckForNotificationSwService } from "./global-service/check-for-notification-sw.service";
import { SharedModule } from "./shared/shared.module";
import { TranslateService } from "./shared/util/translate.service";
import { LayoutModule } from "./shared/layout/layout.module";
import { SharedComponentsModule } from "./shared/components/shared-components.module";
import { ServiceWorkerUpdateComponent } from "./shared/components/service-worker-update/service-worker-update.component";
import { ModalComponent } from "./shared/components/modal/modal.component";
import { providePrimeNG } from "primeng/config";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { MessageService } from "primeng/api";
import Aura from "@primeng/themes/aura";
import { environment } from "src/environments/environment";
import { ServiceWorkerModule } from "@angular/service-worker";
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module, ReCaptchaV3Service } from "ng-recaptcha-2";

/**
 *
 * @param envService Service responsabile del caricamento del file di configurazione
 * @returns File di configurazione.
 * Angular fornisce la possibilità di eseguire delle logiche di configurazione prima che l'applicazione venga inizializzata,
 * in questo modo possiamo caricare il file di configurazione delle variabili globali senza dover ricompilare l'applicazione.
 *
 */
const initAppFn = (envService: EnvironmentLoaderService) => {
  return () => envService.setAppConfig();
};

const translationInitializer = (translation: TranslateService) => {
  return () => {
    translation.loadTranslation();
  };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const notificationInitializer = (_notificationService: CheckForNotificationSwService) => {
  return () => {
    // Initialize the notification service (it will auto-start listening)
    // The service is injected to ensure it's instantiated at startup
    return Promise.resolve();
  };
};



@NgModule({
  declarations: [
    AppComponent,
    BaseComponent
  ],
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    BrowserModule,
    AppRoutingModule,
    BaseModule,
    ReactiveFormsModule,
    LayoutModule,
    SharedComponentsModule,
    RecaptchaV3Module,
    ServiceWorkerUpdateComponent,
    ModalComponent,
    ServiceWorkerModule.register("notification-worker.js", {
      enabled: true, // Enable in both development and production
      // Register the ServiceWorker as soon as the application is stable
      // or after 10 seconds (whichever comes first) - shorter timeout
      registrationStrategy: "registerWhenStable:10000"
    })
  ],
  providers: [
    BackendService,
    TokenStorageService,
    ApplicationDataService,
    TokenManagerService,
    UserService,
    TenantService,
    LoggerService,
    EnvironmentLoaderService,
    provideAppInitializer(() => {
      const initializerFn = (initAppFn)(inject(EnvironmentLoaderService));
      return initializerFn();
    }),
    TranslateService,
    MessageService,
    provideAppInitializer(() => {
      const initializerFn = (translationInitializer)(inject(TranslateService));
      initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = (notificationInitializer)(inject(CheckForNotificationSwService));
      return initializerFn();
    }),
    {
      provide: ErrorHandler,
      useClass: GlobalError
    },
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        preset: Aura,
        options: {
          darkModeSelector: false,
          cssLayer: {
            name: "primeng",
            order: "tailwind-base, primeng, tailwind-utilities"
          }
        }
      },
      translation: {
        "accept": "Si",
        "addRule": "Aggiungi regola",
        "am": "AM",
        "apply": "Applica",
        "cancel": "Annulla",
        "choose": "Scegli",
        "chooseDate": "Seleziona Data",
        "chooseMonth": "Seleziona Mese",
        "chooseYear": "Seleziona Anno",
        "clear": "Cancella tutto",
        "contains": "Contiene",
        "dateAfter": "La data è successiva",
        "dateBefore": "La data è precedente",
        "dateFormat": "dd/mm/yy",
        "dateIs": "La data è",
        "dateIsNot": "La data non è",
        "dayNames": [
          "Domenica",
          "Lunedi",
          "Martedì",
          "Mercoledì",
          "Giovedì",
          "Venerdì",
          "Sabato"
        ],
        "dayNamesMin": [
          "Do",
          "Lu",
          "Ma",
          "Me",
          "Gi",
          "Ve",
          "Sa"
        ],
        "dayNamesShort": [
          "Dom",
          "Lun",
          "Mar",
          "Mer",
          "Gio",
          "Ven",
          "Sab"
        ],
        "emptyFilterMessage": "Nessuna opzione disponibile",
        "emptyMessage": "Nessun risultato trovato",
        "emptySearchMessage": "Nessun risultato trovato",
        "emptySelectionMessage": "Nessun elemento selezionato",
        "endsWith": "Finisce con",
        "equals": "Equivale",
        "fileSizeTypes": [
          "B",
          "KB",
          "MB",
          "GB",
          "TB",
          "PB",
          "EB",
          "ZB",
          "YB"
        ],
        "firstDayOfWeek": 1,
        "gt": "Maggiore di",
        "gte": "Maggiore o uguale a",
        "lt": "Minore di",
        "lte": "Minore o uguale a",
        "matchAll": "Abbina tutto",
        "matchAny": "Abbina alcuni",
        "medium": "Medio",
        "monthNames": [
          "Gennaio",
          "Febbraio",
          "Marzo",
          "Aprile",
          "Maggio",
          "Giugno",
          "Luglio",
          "Agosto",
          "Settembre",
          "Ottobre",
          "Novembre",
          "Dicembre"
        ],
        "monthNamesShort": [
          "Gen",
          "Feb",
          "Mar",
          "Apr",
          "Mag",
          "Giu",
          "Lug",
          "Ago",
          "Set",
          "Ott",
          "Nov",
          "Dic"
        ],
        "nextDecade": "Decade successiva",
        "nextHour": "Ora successiva",
        "nextMinute": "Minuto successivo",
        "nextMonth": "Mese successivo",
        "nextSecond": "Secondo successivo",
        "nextYear": "Anno successivo",
        "noFilter": "Senza Filtro",
        "notContains": "Non contiene",
        "notEquals": "Non uguale",
        "passwordPrompt": "Inserisci la password",
        "pending": "In corso",
        "pm": "PM",
        "prevDecade": "Decade precedente",
        "prevHour": "Ora precedente",
        "prevMinute": "Minuto precedente",
        "prevMonth": "Mese precedente",
        "prevSecond": "Secondo precedente",
        "prevYear": "Anno precedente",
        "reject": "No",
        "removeRule": "Rimuovi regola",
        "searchMessage": "{0} risultati disponibili",
        "selectionMessage": "{0} elementi selezionati",
        "startsWith": "Inizia con",
        "strong": "Forte",
        "today": "Oggi",
        "upload": "Carica",
        "weak": "Debole",
        "weekHeader": "Sett",
        "aria": {
          "cancelEdit": "Annulla modifica",
          "close": "Chiudi",
          "collapseRow": "Riduci riga",
          "editRow": "Modifica riga",
          "expandRow": "Espandi riga",
          "falseLabel": "Falso",
          "filterConstraint": "Costante di filtro",
          "filterOperator": "Operatore di filtro",
          "firstPageLabel": "Prima pagina",
          "gridView": "Griglia",
          "hideFilterMenu": "Nascondi Menu filtri",
          "jumpToPageDropdownLabel": "Vai alla Dropdown delle pagine",
          "jumpToPageInputLabel": "Vai all'Input delle pagine",
          "lastPageLabel": "Ultima pagina",
          "listView": "Lista",
          "moveAllToSource": "Muovi tutto alla sorgente",
          "moveAllToTarget": "Muovi tutto all'elemento",
          "moveBottom": "Vai in fondo",
          "moveDown": "Vai sotto",
          "moveTop": "Vai in cima",
          "moveToSource": "Vai alla sorgente",
          "moveToTarget": "Vai all'elemento",
          "moveUp": "Vai sopra",
          "navigation": "Naviga",
          "next": "Successivo",
          "nextPageLabel": "Pagina successiva",
          "nullLabel": "Non selezionato",
          "pageLabel": "Pagina {page}",
          "previous": "Precedente",
          "previousPageLabel": "Pagina precedente",
          "removeLabel": "Rimuovere",
          "rotateLeft": "Ruota a sinistra",
          "rotateRight": "Ruota a destra",
          "rowsPerPageLabel": "Elementi per pagina",
          "saveEdit": "Salva modifica",
          "scrollTop": "Torna sù",
          "selectAll": "Seleziona tutti gli elementi",
          "selectRow": "Seleziona riga",
          "showFilterMenu": "Mostra Menu filtri",
          "slide": "Scorri",
          "slideNumber": "{slideNumber}",
          "star": "1 stella",
          "stars": "{star} stelle",
          "trueLabel": "Vero",
          "unselectAll": "Deseleziona tutti gli elementi",
          "unselectRow": "Deseleziona riga",
          "zoomImage": "Zoom Immagine",
          "zoomIn": "Ingrandisci",
          "zoomOut": "Riduci"
        }
      }
    }),
    ReCaptchaV3Service,
    {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptchaKey
    },
    i18nModule.setLocale(),
    i18nModule.setLocaleId()
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
